import React, { useEffect, useMemo, useState } from "react";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const pad2 = (n) => String(n).padStart(2, "0");

const HOUR_OPTS = Array.from({ length: 24 }, (_, i) => pad2(i));
const MINUTE_OPTS = Array.from({ length: 60 }, (_, i) => pad2(i));

function parseHm(value) {
  if (!value || typeof value !== "string") return null;
  const m = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return { h: pad2(h), m: pad2(min) };
}

/**
 * Popover hour/minute picker — replaces native `<input type="time">` so the surface matches
 * `DatePickerField` / calendar popovers (`t-glass`, eyebrow chrome).
 * Values are 24h `HH:MM` strings; empty string means no time.
 */
export function TimePickerField({
  value,
  onChange,
  placeholder = "Select time",
  popoverTitle = "Time",
  "data-testid": testId,
  triggerClassName,
}) {
  const [open, setOpen] = useState(false);
  const parsed = useMemo(() => parseHm(value), [value]);
  const [hour, setHour] = useState(parsed?.h ?? "12");
  const [minute, setMinute] = useState(parsed?.m ?? "00");

  useEffect(() => {
    if (!open) return;
    const next = parseHm(value);
    if (next) {
      setHour(next.h);
      setMinute(next.m);
    } else {
      setHour("12");
      setMinute("00");
    }
  }, [open, value]);

  const display = parsed ? `${parsed.h}:${parsed.m}` : null;

  const commit = (h, m) => {
    onChange(`${h}:${m}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            "t-input flex min-h-[48px] w-full min-w-0 cursor-pointer items-center justify-between gap-2 text-left !rounded-[var(--t-event-pill-radius)] bg-[#FAFBFC] px-4 font-light outline-none transition-[border-color] duration-[160ms] ease-out",
            display ? "text-[var(--t-text)]" : "text-[var(--t-meta)]",
            triggerClassName,
          )}
        >
          <span>{display ?? placeholder}</span>
          <Clock size={15} strokeWidth={1.4} className="pointer-events-none shrink-0 text-[var(--t-meta)]" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        collisionPadding={16}
        className="z-[70] min-w-[260px] max-w-[calc(100vw-2rem)] w-auto rounded-none p-0 text-[var(--t-text)] t-glass"
      >
        <div
          className={cn(
            "flex items-center gap-3 border-b border-[var(--t-border)] p-3",
            popoverTitle ? "justify-between" : "justify-end",
          )}
        >
          {popoverTitle ? <div className="label-eyebrow">{popoverTitle}</div> : null}
          <button
            type="button"
            className="t-btn-ghost px-2 py-1 text-xs"
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            data-testid={testId ? `${testId}-popover-clear` : undefined}
          >
            Clear
          </button>
        </div>
        <div className="flex gap-3 p-4">
          <div className="min-w-0 flex-1">
            <label className="label-eyebrow mb-1 block" htmlFor={testId ? `${testId}-hour` : undefined}>
              Hour
            </label>
            <select
              id={testId ? `${testId}-hour` : undefined}
              className="t-select w-full min-w-0 text-sm"
              value={hour}
              onChange={(e) => {
                const h = e.target.value;
                setHour(h);
                commit(h, minute);
              }}
              data-testid={testId ? `${testId}-hour` : undefined}
            >
              {HOUR_OPTS.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-0 flex-1">
            <label className="label-eyebrow mb-1 block" htmlFor={testId ? `${testId}-minute` : undefined}>
              Minute
            </label>
            <select
              id={testId ? `${testId}-minute` : undefined}
              className="t-select w-full min-w-0 text-sm"
              value={minute}
              onChange={(e) => {
                const m = e.target.value;
                setMinute(m);
                commit(hour, m);
              }}
              data-testid={testId ? `${testId}-minute` : undefined}
            >
              {MINUTE_OPTS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
